import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

interface WeekSelectorProps {
  selectedDate: Date
  onDateChange: (date: Date) => void
}

function getMonday(d: Date): Date {
  const copy = new Date(d)
  copy.setHours(0, 0, 0, 0)
  const day = copy.getDay()
  const diff = day === 0 ? -6 : 1 - day
  copy.setDate(copy.getDate() + diff)
  return copy
}

export function WeekSelector({ selectedDate, onDateChange }: WeekSelectorProps) {
  const { t } = useTranslation()
  const dayAbbr = t('mobile.dayAbbr', { returnObjects: true }) as string[]

  const weekDays = useMemo(() => {
    const mon = getMonday(selectedDate)
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(mon)
      d.setDate(mon.getDate() + i)
      return d
    })
  }, [selectedDate])

  const today = useMemo(() => {
    const t = new Date()
    t.setHours(0, 0, 0, 0)
    return t.getTime()
  }, [])

  const selNorm = useMemo(() => {
    const d = new Date(selectedDate)
    d.setHours(0, 0, 0, 0)
    return d.getTime()
  }, [selectedDate])

  const goWeek = (delta: number) => {
    const next = new Date(selectedDate)
    next.setDate(next.getDate() + delta * 7)
    onDateChange(next)
  }

  return (
    <div className="flex items-center gap-1 px-2">
      <button
        onClick={() => goWeek(-1)}
        className="shrink-0 rounded-lg p-1.5 text-neutral-400 transition-colors active:bg-neutral-100"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <div className="flex flex-1 justify-between">
        {weekDays.map((date, i) => {
          const time = date.getTime()
          const isSelected = time === selNorm
          const isToday = time === today

          return (
            <button
              key={i}
              onClick={() => onDateChange(new Date(date))}
              className={cn(
                'flex flex-1 flex-col items-center gap-0.5 rounded-xl py-1.5 transition-all',
                isSelected
                  ? 'bg-primary/15 ring-2 ring-primary/30'
                  : isToday
                    ? 'bg-primary/5 ring-1 ring-primary/15'
                    : 'active:bg-neutral-100',
              )}
            >
              <span
                className={cn(
                  'text-[10px] font-bold uppercase tracking-wide',
                  isSelected ? 'text-primary' : 'text-neutral-400',
                )}
              >
                {dayAbbr[i]}
              </span>
              <span
                className={cn(
                  'text-sm leading-none',
                  isSelected ? 'font-bold text-primary' : isToday ? 'font-semibold text-neutral-800' : 'font-medium text-neutral-600',
                )}
              >
                {date.getDate()}
              </span>
            </button>
          )
        })}
      </div>

      <button
        onClick={() => goWeek(1)}
        className="shrink-0 rounded-lg p-1.5 text-neutral-400 transition-colors active:bg-neutral-100"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  )
}
